import { useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { Deal } from '../../types';
function generateColors(count: number): string[] {
  const goldenAngle = 137.508;
  return Array.from({
    length: count
  }, (_, i) => {
    const hue = i * goldenAngle % 360;
    return `hsl(${Math.round(hue)}, 65%, 50%)`;
  });
}
const AEM_EXCEPTIONS: Record<string, string> = {
  'AEM: SITES OPTIMIZER': 'Sites Optimizer',
  'AEM: BRAND CONCIERGE': 'Brand Concierge',
  'AEM: LLM OPTIMIZER': 'LLM Optimizer'
};
const PRODUCT_SYNONYMS: Record<string, string> = {
  'MARKETO: ENGAGE': 'Marketo',
  'MARKETO: MEASURE': 'Marketo',
  'MARKETO MEASURE': 'Marketo',
  'MARKETO': 'Marketo',
  'COMMERCE': 'Commerce',
  'WKFT': 'Workfront',
  'WORKFRONT': 'Workfront',
  'ADOBE ACROBAT SIGN': 'Adobe Acrobat',
  'ACROBAT SERVICES API': 'Adobe Acrobat',
  'ADOBE ACROBAT SERVICES': 'Adobe Acrobat'
};
export function bucketSolution(products: string): string {
  if (!products || products.trim() === '') return 'Unknown';
  const parts = products.split(',').map(p => p.trim()).filter(Boolean);
  const normed = [...new Set(parts.map(p => {
    const upper = p.toUpperCase();
    const bucketed = AEM_EXCEPTIONS[upper] ?? (upper.startsWith('AEM') ? 'AEM' : p.trim());
    const synonymKey = bucketed.toUpperCase();
    return PRODUCT_SYNONYMS[synonymKey] ?? bucketed;
  }))];
  return normed.length === 1 ? normed[0] : 'Multi-Solution';
}
interface Props {
  deals: Deal[];
  top: number;
  onTopChange: (v: number) => void;
  onTotalChange?: (v: number) => void;
  chartHeight?: number;
  selected?: string | null;
  onSelect?: (name: string) => void;
  onOthersChange?: (labels: string[]) => void;
}
export function OfferingChart({
  deals,
  top,
  onTopChange,
  onTotalChange,
  chartHeight = 220,
  selected,
  onSelect,
  onOthersChange
}: Props) {
  const solMap: Record<string, number> = {};
  const seenPerBucket: Record<string, Set<string>> = {};
  deals.forEach(d => {
    const sol = bucketSolution(d.products || '');
    if (!seenPerBucket[sol]) seenPerBucket[sol] = new Set();
    if (seenPerBucket[sol].has(d.requestID)) return;
    seenPerBucket[sol].add(d.requestID);
    solMap[sol] = (solMap[sol] || 0) + 1;
  });
  const solSorted = Object.entries(solMap).sort((a, b) => b[1] - a[1]);
  const totalBuckets = solSorted.length;
  useEffect(() => {
    onTotalChange?.(totalBuckets);
  }, [totalBuckets, onTotalChange]);
  let labels: string[], vals: number[];
  const othersLabels = totalBuckets > top ? solSorted.slice(top).map(([l]) => l) : [];
  if (totalBuckets > top) {
    const topSlices = solSorted.slice(0, top);
    const othersCount = solSorted.slice(top).reduce((s, [, v]) => s + v, 0);
    labels = [...topSlices.map(([l]) => l), 'Others'];
    vals = [...topSlices.map(([, v]) => v), othersCount];
  } else {
    labels = solSorted.map(([l]) => l);
    vals = solSorted.map(([, v]) => v);
  }
  useEffect(() => {
    onOthersChange?.(othersLabels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [othersLabels.join('|'), onOthersChange]);
  const data = labels.map((name, i) => ({
    name,
    value: vals[i]
  }));
  const colors = generateColors(data.length);
  const mid = Math.ceil(data.length / 2);
  const col1 = data.slice(0, mid);
  const col2 = data.slice(mid);
  const handleClick = (name: string) => onSelect?.(name);
  const renderLegendCol = (items: typeof data) => <ul style={{
    listStyle: 'none',
    padding: 0,
    margin: 0
  }} data-id="element-89" data-mp="3wl7y1ucspvz">
      {items.map((item, i) => {
      const colorIdx = data.indexOf(item);
      const isSelected = selected === item.name;
      const isDimmed = !!selected && !isSelected;
      return <li key={i} onClick={() => handleClick(item.name)} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginBottom: 2,
        fontSize: 9,
        color: isSelected ? '#111827' : '#374151',
        fontWeight: isSelected ? 700 : 400,
        opacity: isDimmed ? 0.45 : 1,
        cursor: 'pointer'
      }} data-id="element-90" data-mp="hurx8tl8lhk1" data-mpi={i}>
            <span style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: colors[colorIdx],
          flexShrink: 0
        }} data-id="element-91" data-mp="1muge6gqm7p6z" />
            <span data-id="element-92" data-mp="20ur85sn81jwd">{item.name} ({item.value})</span>
          </li>;
    })}
    </ul>;
  return <div style={{
    display: 'flex',
    alignItems: 'center'
  }} data-id="element-93" data-mp="1wqb29rdlych5">
      <ResponsiveContainer width="50%" height={chartHeight} data-id="element-94" data-mp="2aqlv2zyha14y">
        <PieChart data-id="element-95" data-mp="2ap11bcjzmm85">
          <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={2} stroke="#fff" onClick={(entry: {
          name: string;
        }) => handleClick(entry.name)} style={{
          cursor: 'pointer'
        }} data-id="element-96" data-mp="25sk9o8dkxpqb">
            {data.map((entry, i) => <Cell key={i} fill={colors[i]} opacity={selected && selected !== entry.name ? 0.35 : 1} data-id="element-97" data-mp="2ois7cayifnz4" data-mpi={i} />)}
          </Pie>
          <Tooltip contentStyle={{
          fontSize: 11,
          border: '1px solid #e8eaf0',
          borderRadius: 6
        }} formatter={(v: number) => [v, 'Deals']} data-id="element-98" data-mp="385lpo9h577yw" />
        </PieChart>
      </ResponsiveContainer>
      <div style={{
      display: 'flex',
      gap: 12,
      flex: 1,
      paddingLeft: 4
    }} data-id="element-99" data-mp="2z3ch3p5fpoo5">
        {renderLegendCol(col1)}
        {col2.length > 0 && renderLegendCol(col2)}
      </div>
    </div>;
}